import {
  Microscope, Stethoscope, Home as HomeIcon, Pill, Syringe,
  GraduationCap, Globe2, Leaf, Trophy, LucideIcon,
} from 'lucide-react';

// Lucide icon per vertical id — replaces the emoji icons for a sharper, more
// professional look while keeping every vertical visually distinct.
export const verticalIcons: Record<string, LucideIcon> = {
  bms: Microscope,
  rmc: Stethoscope,
  rhms: HomeIcon,
  rsl: Microscope,
  rp: Pill,
  rsp: Syringe,
  academy: GraduationCap,
  impex: Globe2,
  gardens: Leaf,
  rsa: Trophy,
};

// Unsplash-hosted stock photography, used purely as placeholders.
// Every <img> in the app renders through <SafeImage>, so if any of these
// links ever go stale the layout still degrades gracefully to a brand
// gradient instead of a broken-image icon.
// >>> Swap any of these URLs for local/client photography whenever ready. <<<
export const stock = {
  mainlogo: '/RG_LOGO.webp',
  founder: '/Dr. K. R. Premchand.png',
  founder_bg: '/Dr. K. R. Premchand_bg.webp',
  muthiah: '/DR.MUTHTHAIH.webp',
  muthurengan: '/DR.MUTHURENGANATHAN.webp',
  dharmaraj: '/DR.DHARMARAJ.webp',
  reshma: '/DR.RESHMA.webp',
  sriram: '/DR.SRIRAM.webp',
  umasankar: '/DR.UMASHANKAR.webp',
  pandeeswari: '/C. PANDEESWARI.webp',
  hema: '/C. HEMA LATHA.webp',
  gowsiya: '/GOWSIYA BEGUM.webp',
  iswarya: '/ISWARYA.webp',
  nandha: '/NANDHA KUMAR.webp',
  aneesh: '/ANEESH.webp',
  reshmabanu: '/RESHMA BANU.webp',
  janani: '/Dr. R. Janani.webp',
  sandravelu: 'Dr. T. Sandravelu.webp',
  annieshennapaul: 'Dr. Annie_Sheena_Paul.webp',
  heroBackdrop: 'https://images.unsplash.com/photo-1666214280391-8ff5bd3c0bf0?q=80&w=1600&auto=format&fit=crop',
  rmc: 'RMC_BG.webp',
  rmsl: '/RSML_BG4_FINAL.webp',
  rsl: 'RSL_BG.webp',
  rhms: 'RHMS_BG.webp',
  handshake: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1200&auto=format&fit=crop',
  teamHuddle: 'TEAM.png',
  communityCamp: 'https://preethihospitals.com/wp-content/uploads/2026/03/Womens-Day-Camp-2026-3rd-Week-15.jpg',
  bloodDonation: 'https://images.unsplash.com/photo-1615461066841-6116e61058f4?q=80&w=1200&auto=format&fit=crop',
  healthCheckup: 'https://img.magnific.com/free-photo/young-male-psysician-with-patient-measuring-blood-pressure_1303-17879.jpg?semt=ais_hybrid&w=740&q=80',
  doctorTablet: 'https://images.unsplash.com/photo-1622902046580-2b47f47f5471?q=80&w=1200&auto=format&fit=crop',
  nurseElder: 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?q=80&w=1200&auto=format&fit=crop',
  pharmacyShelf: 'R_PHARMA_BG.webp',
  sportsField: 'RSA_BG3.webp',
  campus: 'RA_BG2.webp',
  garden: 'RG_BG.webp',
  logistics: 'R_IMPEX_BG.webp',
  rsp: 'RSP_BG5.webp',
  mapArea: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=1200&auto=format&fit=crop',

  // HEALTY COMMUNITY HEROES — placeholder images for the "Healthy Community Heroes" section on the homepage.
  hch1: 'HC1.webp',
  hch2: 'HC2.webp',
  hch3: 'HC3.webp',
  hch4: 'HC4.webp',

  tl1: 'TL1.webp',
  tl2: 'TL2.webp',
  tl3: 'TL3.webp',
  tl4: 'TL4.webp',

  // Portrait placeholders for the team grid on the About page.
  portraits: [
    'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?q=80&w=400&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1594824476967-48c8b964273f?q=80&w=400&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=400&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1622253692010-333f2da6031d?q=80&w=400&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1537368910025-700350fe46c7?q=80&w=400&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1607990283143-e81e7a2c9349?q=80&w=400&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=400&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=400&auto=format&fit=crop',
  ],
};

// Vertical logo images, keyed by the vertical's `code` (case may differ from
// verticals.ts's own `code` field — Bookshelf looks this up case-insensitively).
export const verticalLogos: Record<string, string> = {
  RMSL: '/RMSL_LOGO.webp',
  RMC: '/RMC_LOGO.webp',
  RHMS: '/RHMS_LOGO.webp',
  RP: '/RP_LOGO.webp',
  RSP: '/RSP_LOGO.webp',
  Academy: '/R_ACADEMY.webp',
  Impex: '/R_IMPEX_LOGO.webp',
  Gardens: '/R_Gardens_LOGO.webp',
  RSA: '/RSA_LOGO.webp',
  RSL: '/RSL_LOGO.webp',
};

// Per-vertical banner image shown on each vertical's detail page hero.
export const verticalHeroImage: Record<string, string> = {
  bms: stock.rmsl,
  rmc: stock.rmc,
  rhms: stock.rhms,
  rp: stock.pharmacyShelf,
  rsp: stock.rsp,
  academy: stock.campus,
  impex: stock.logistics,
  gardens: stock.garden,
  rsa: stock.sportsField,
  rsl: stock.rsl,
};

// Placeholder hero/ambient video — a calm, generic clip so the hero section
// has real motion out of the box. Swap for an on-site / brand video anytime;
// SafeVideo below fails silently to a static image if this link ever breaks.
export const stockVideo = 'https://assets.mixkit.co/videos/preview/mixkit-doctor-with-a-tablet-in-a-hospital-hallway-4809-large.mp4';
